import { ITheme, IStyle, mergeStyleSets, getTheme } from '@fluentui/react';
import React, { useMemo } from 'react';
import { IProductDetailDialogContentProps } from './content';
import { RatingBar } from '../../../app-shop/components/rating-bar';
import { BwjButton, BwjButtonType } from '../../../framework/components/bwj-button';
import { InfoLine } from '../../../app-shop/shop/components/info-line';
import { ProductImageBrowser } from '../../../app-shop/shop/components/pdct-image-browser';
import { MeaurementRequestNote } from './measure-req-note';
import { ProductVariantSelector } from '../../../app-shop/shop/components/pdct-variant-select';
import { isMeaningfulString } from '../../../framework/utils/string';
import { RatingBreakdown } from '../../../app-shop/shop/components/rating-breakdown';
import { MeasurementRequestForm } from '../measurement-request-form';

const getStyleClasses = (theme: ITheme) => {
  const normalSpace = '0.75rem';
  const bigSpace = '1rem';
  const tinySpace = '0.1rem';
  const megaSpace = '2.5rem';

  const root: IStyle = {
    width: '100%',
    maxWidth: 500,
    overflow: 'hidden',
    margin: '0 auto',
    padding: 0,
  };

  const brand: IStyle = {
    fontSize: theme.fonts.xSmall.fontSize,
    lineHeight: theme.fonts.xSmall.lineHeight,
    fontWeight: 400,
    margin: 0,
    padding: 0,
  };

  const title: IStyle = {
    fontSize: theme.fonts.mediumPlus.fontSize,
    lineHeight: theme.fonts.mediumPlus.lineHeight,
    fontWeight: 600,
    margin: 0,
    padding: 0,
    marginBottom: normalSpace,
  };

  const keyStatsContainer: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    gap: bigSpace,
    alignItems: 'flex-start',
  };

  const ratingContainer: IStyle = {
    width: 1,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  };

  const mediaContainer: IStyle = {
    marginTop: bigSpace,
    marginBottom: bigSpace,
  }

  const variantSelector: IStyle = {
    marginBottom: bigSpace,
  };

  const subheading: IStyle = {
    fontSize: theme.fonts.medium.fontSize,
    lineHeight: theme.fonts.medium.lineHeight,
    fontWeight: 600,
    margin: 0,
    padding: 0,
    marginTop: bigSpace,
    marginBottom: normalSpace,
  };

  const actionContainer: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: megaSpace,
    marginBottom: megaSpace,
    gap: bigSpace,
  }

  const buttonContainer: IStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: tinySpace,
  };

  const section: IStyle = {
    margin: 0,
    padding: 0,

    '& > ul': {
      margin: 0,
    },

    '& p': {
      margin: 0,
      padding: 0,
      marginBottom: normalSpace,
    },
  }

  const reviewContainer: IStyle = {};

  return mergeStyleSets({
    root,
    brand,
    title,
    mediaContainer,
    keyStatsContainer,
    ratingContainer,
    variantSelector,
    subheading,
    actionContainer,
    buttonContainer,
    section,
    reviewContainer,
  });
}

export const ProductDetailDialogContentMobile: React.FunctionComponent<IProductDetailDialogContentProps> = (props) => {
  const theme = getTheme();
  const cs = getStyleClasses(theme);
  const { contentWidth } = props;
  const mediaWidth = useMemo(() => contentWidth <= 500 ? contentWidth : 500, [contentWidth]);

  return (
    <>
    {props.showMeasurementRequestForm === false ?
    <div className={cs.root}>
      <h3 className={cs.brand}>{props.brand}</h3>
      <h2 className={cs.title}>{props.title}</h2>
      {!!props.rating && !!props.averageRating &&
      <div className={cs.keyStatsContainer}>
        <RatingBar
          rating={props.averageRating}
          reviewerCount={props.rating.totalReviewCount}
          rootStyle={{ width: '100%', maxWidth: 200 }}
        />
      </div>}
      <div className={cs.mediaContainer}>
        <ProductImageBrowser
          id={props.id}
          idPrefix='prd-dtl'
          images={props.imageUrls}
          imageWidth={mediaWidth}
        />
      </div>
      {props.variants.length > 1 &&
      <div className={cs.variantSelector}>
        <h3 className={cs.variantSelector}>
          {`Available ${props.variantDescriptor}:`}
        </h3>
        <ProductVariantSelector
          swatchSize={5}
          options={props.variants}
          selected={props.selectedVariant}
          showLabels={true}
          onSelectionChanged={s => !!s && props.onVariantSelected(s)}
        />
      </div>}

      <div className={cs.actionContainer}>
        {props.measurementRequestDate === null ?
        <div>
          <BwjButton
            buttonStyle={BwjButtonType.PrimaryStandard}
            text={'Request pricing'}
            onClick={() => props.onToggleMeasurementRequestForm(true)}
          />
          <InfoLine
            icon={'LightBulb'}
            text={'Why can\'t you just tell me the price?'}
            onClick={props.onShowPricingExplanation}
          />
        </div>
        :
        <MeaurementRequestNote requestDate={props.measurementRequestDate} />}
      </div>

      <section className={cs.section}>
        <h3 className={cs.subheading}>Key Features</h3>
        <ul>
          {props.keyFeatures.map(feature =>
          <li key={feature}>{feature}</li>)}
        </ul>
      </section>
      {!!props.rating && !!props.averageRating &&
      <section className={cs.section}>
        <h3 className={cs.subheading}>{`Average Customer Rating: ${props.averageRating.toFixed(1)}`}</h3>
        <div className={cs.reviewContainer}>
          {isMeaningfulString(props.rating.reviewSummary) &&
          <p>{props.rating.reviewSummary}</p>}
          <RatingBreakdown review={props.rating} />
        </div>
      </section>}
    </div>
    :
    <div className={cs.root}>
      <MeasurementRequestForm
        formModel={props.requestMeasurementFormModel}
        onRequestSubmitted={props.onMeasurementRequestSubmitted}
        onDismiss={() => props.onToggleMeasurementRequestForm(false)}
      />
    </div>}
    </>
  );
};